import httpClient from '@frontend/api/http';
import {
  AgentExecutionsResponse,
  BulkCancelResponse,
  CancellableExecutionIdsResponse,
  ExecutionDetails,
  MemoryDetails,
  UnifiedExecutionListResponse,
} from '@frontend/types/agentExecution';
import { MemoryChunk, SpecializedAgentExecutionsResponse } from '@frontend/types/specialized-agent';

export type UnifiedExecutionsParams = {
  instanceId: string;
  page?: number;
  pageSize?: number;
  agentType?: string;
  status?: string;
  searchQuery?: string;
  userId?: string[];
  agentId?: string;
  dateFrom?: string;
  dateTo?: string;
};

export const agentExecutionsService = {
  getUnifiedExecutions: async ({
    instanceId,
    page = 1,
    pageSize = 20,
    agentType,
    status,
    searchQuery,
    userId,
    agentId,
    dateFrom,
    dateTo,
  }: UnifiedExecutionsParams): Promise<UnifiedExecutionListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (agentType) params.set('agent_type', agentType);
    if (status) params.set('status', status);
    if (searchQuery) params.set('search_query', searchQuery);
    userId?.forEach(id => id && params.append('user_id', id));
    if (agentId) params.set('agent_id', agentId);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);

    const response = await httpClient.get(
      `/hypatia/api/v1/${instanceId}/agent-executions?${params}`
    );
    // @ts-expect-error: FIXME: OPAL-2388
    return response.data;
  },

  // Get all workflow executions
  getExecutions: async ({
    instanceId,
    agentId,
    page = 1,
    pageSize = 100,
    status,
    searchQuery,
  }: {
    instanceId: string;
    agentId?: string;
    page?: number;
    pageSize?: number;
    status?: string;
    searchQuery?: string;
  }): Promise<AgentExecutionsResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (agentId) params.set('workflow_agent_id', agentId);
    if (status) params.set('status', status);
    if (searchQuery) params.set('search_query', searchQuery);

    const url = `/hypatia/api/v1/${instanceId}/workflow-executions?${params}`;

    try {
      const response = await httpClient.get(url);

      // Check if response is HTML instead of JSON
      if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
        console.error('agentExecutionsService.getExecutions - received HTML instead of JSON');
        throw new Error('API returned HTML instead of JSON - likely endpoint not found');
      }

      // @ts-expect-error: FIXME: OPAL-2388
      return response.data;
    } catch (error) {
      console.error('agentExecutionsService.getExecutions - error:', error);
      throw error;
    }
  },

  // Get executions for specific agent
  getAgentExecutions: async (
    instanceId: string,
    agentId: string,
    page = 1,
    pageSize = 100,
    agentVersion?: number | null
  ): Promise<SpecializedAgentExecutionsResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    // Add agent_version if provided
    if (agentVersion !== null && agentVersion !== undefined) {
      params.append('agent_version', agentVersion.toString());
    }

    const response = await httpClient.get(
      `/hypatia/api/v1/${instanceId}/agents/specialized/${agentId}/executions?${params.toString()}`
    );
    // @ts-expect-error: FIXME: OPAL-2388
    return response.data;
  },

  // Lightweight total_count for an agent over an optional date range (page_size=1).
  // Powers the export dialog's live banner. Works for both agent types.
  getExecutionRangeCount: async ({
    instanceId,
    agentType,
    agentId,
    startDate,
    endDate,
  }: {
    instanceId: string;
    agentType: 'specialized' | 'workflow';
    agentId: string;
    startDate?: string;
    endDate?: string;
  }): Promise<number> => {
    const params = new URLSearchParams({ page: '1', page_size: '1' });
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (agentType === 'workflow') params.set('workflow_agent_id', agentId);

    const url =
      agentType === 'workflow'
        ? `/hypatia/api/v1/${instanceId}/workflow-executions?${params}`
        : `/hypatia/api/v1/${instanceId}/agents/specialized/${agentId}/executions?${params}`;

    const response = await httpClient.get(url);
    const data = response.data as { pagination?: { total_count?: number } } | undefined;
    return data?.pagination?.total_count ?? 0;
  },

  // Get execution details using HATEOAS link
  getExecutionDetails: async (executionUrl: string): Promise<ExecutionDetails> => {
    try {
      const response = await httpClient.get(executionUrl);
      // @ts-expect-error: FIXME: OPAL-2388
      return response.data;
    } catch (error) {
      console.error('getExecutionDetails - error:', error);
      throw error;
    }
  },

  // Submit a whole-execution thumbs rating for an organic workflow execution.
  // PATCHes the execution's HATEOAS self link (status is optional server-side, so a
  // rating-only update is accepted). `thumbsRating` is +1 / -1 (or null to clear).
  updateExecutionRating: async (
    executionUrl: string,
    thumbsRating: number | null
  ): Promise<ExecutionDetails> => {
    try {
      const response = await httpClient.patch(executionUrl, { thumbs_rating: thumbsRating });
      // @ts-expect-error: FIXME: OPAL-2388
      return response.data;
    } catch (error) {
      console.error('updateExecutionRating - error:', error);
      throw error;
    }
  },

  // Get memory details using HATEOAS link
  getMemoryDetails: async (memoryUrl: string): Promise<MemoryDetails> => {
    try {
      const response = await httpClient.get(memoryUrl);
      // @ts-expect-error: FIXME: OPAL-2388
      return response.data;
    } catch (error) {
      console.error('getMemoryDetails - error:', error);
      throw error;
    }
  },

  // Get memory chunks using HATEOAS link
  getMemoryChunks: async (chunksUrl: string): Promise<MemoryChunk[]> => {
    try {
      const response = await httpClient.get(chunksUrl);
      // @ts-expect-error: FIXME: OPAL-2388
      return response.data;
    } catch (error) {
      console.error('getMemoryChunks - error:', error);
      throw error;
    }
  },

  // Cancel workflow execution
  cancelWorkflowExecution: async (instanceId: string, executionId: string): Promise<void> => {
    try {
      // Use proper POST endpoint instead of PATCH
      await httpClient.post(
        `/hypatia/api/v1/${instanceId}/workflow-executions/${executionId}/cancel`
      );
    } catch (error) {
      console.error('cancelWorkflowExecution - error:', error);
      throw error;
    }
  },

  // Resume workflow execution
  resumeWorkflowExecution: async (instanceId: string, executionId: string): Promise<void> => {
    try {
      await httpClient.post(
        `/hypatia/api/v1/${instanceId}/workflow-executions/${executionId}/resume`
      );
    } catch (error) {
      console.error('resumeWorkflowExecution - error:', error);
      throw error;
    }
  },

  bulkCancelExecutions: async (
    instanceId: string,
    executionIds: string[]
  ): Promise<BulkCancelResponse> => {
    const response = await httpClient.post(
      `/hypatia/api/v1/${instanceId}/agent-executions/bulk-cancel`,
      { execution_ids: executionIds }
    );
    // @ts-expect-error: FIXME: OPAL-2388
    return response.data;
  },

  // Resolve all cancellable (running/pending) execution ids matching the filters
  getCancellableExecutionIds: async ({
    instanceId,
    agentType,
    searchQuery,
    userId,
    agentId,
    dateFrom,
    dateTo,
  }: Omit<
    UnifiedExecutionsParams,
    'page' | 'pageSize' | 'status'
  >): Promise<CancellableExecutionIdsResponse> => {
    const params = new URLSearchParams();
    if (agentType) params.set('agent_type', agentType);
    if (searchQuery) params.set('search_query', searchQuery);
    userId?.forEach(id => id && params.append('user_id', id));
    if (agentId) params.set('agent_id', agentId);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);

    const response = await httpClient.get(
      `/hypatia/api/v1/${instanceId}/agent-executions/cancellable-ids?${params}`
    );
    // @ts-expect-error: FIXME: OPAL-2388
    return response.data;
  },

  // Get output evaluation metrics
  getOutputEvaluationMetrics: async (
    instanceId: string,
    agentId: string,
    agentVersion?: number | null,
    baselineScore = 70
  ): Promise<{
    agent_id: string;
    total_executions: number;
    avg_quality_score: number;
    passed: number;
    failed: number;
    not_evaluated: number;
  }> => {
    const params = new URLSearchParams({
      baseline_score: baselineScore.toString(),
    });

    // Add agent_version if provided
    if (agentVersion !== null && agentVersion !== undefined) {
      params.append('agent_version', agentVersion.toString());
    }

    const response = await httpClient.get(
      `/hypatia/api/v1/${instanceId}/agents/specialized/${agentId}/executions/evaluation-metrics?${params.toString()}`
    );
    // @ts-expect-error: FIXME: OPAL-2388
    return response.data;
  },
};
